/**
 * Runge-Kutta 4-tartib, qat'iy qadam. dy/dt = f(t, y).
 * Dinamik rejim uchun. Lokal xatolik O(dt⁵), global O(dt⁴).
 */

export type Derivative = (t: number, y: readonly number[]) => number[];

export function rk4Step(f: Derivative, t: number, y: readonly number[], dt: number): number[] {
  const n = y.length;
  const k1 = f(t, y);
  const y2 = new Array<number>(n);
  for (let i = 0; i < n; i++) y2[i] = (y[i] as number) + 0.5 * dt * (k1[i] as number);
  const k2 = f(t + 0.5 * dt, y2);
  const y3 = new Array<number>(n);
  for (let i = 0; i < n; i++) y3[i] = (y[i] as number) + 0.5 * dt * (k2[i] as number);
  const k3 = f(t + 0.5 * dt, y3);
  const y4 = new Array<number>(n);
  for (let i = 0; i < n; i++) y4[i] = (y[i] as number) + dt * (k3[i] as number);
  const k4 = f(t + dt, y4);
  const out = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    out[i] =
      (y[i] as number) +
      (dt / 6) *
        ((k1[i] as number) + 2 * (k2[i] as number) + 2 * (k3[i] as number) + (k4[i] as number));
  }
  return out;
}

export interface IntegrateOptions {
  /** Har qadamdagi holatni saqlash (grafik uchun) */
  record?: boolean;
  /** Har qadamdan keyin chaqiriladi; false qaytarsa integratsiya to'xtaydi */
  onStep?: (t: number, y: readonly number[]) => boolean | void;
}

export interface Trajectory {
  t: number[];
  y: number[][];
  final: number[];
  tFinal: number;
  steps: number;
}

/**
 * [t0, tEnd] oraliqda qat'iy `dt` qadam bilan integratsiya.
 * Oxirgi qadam tEnd ga to'g'ri tushishi uchun qisqartiriladi.
 */
export function integrate(
  f: Derivative,
  y0: readonly number[],
  t0: number,
  tEnd: number,
  dt: number,
  opts: IntegrateOptions = {},
): Trajectory {
  if (dt <= 0) throw new Error('integrate: dt must be > 0');
  const ts: number[] = [];
  const ys: number[][] = [];
  let t = t0;
  let y = [...y0];
  if (opts.record) {
    ts.push(t);
    ys.push([...y]);
  }
  let steps = 0;
  while (t < tEnd - 1e-12 * Math.max(1, Math.abs(tEnd))) {
    const h = Math.min(dt, tEnd - t);
    y = rk4Step(f, t, y, h);
    t += h;
    steps++;
    if (opts.record) {
      ts.push(t);
      ys.push([...y]);
    }
    if (opts.onStep && opts.onStep(t, y) === false) break;
  }
  return { t: ts, y: ys, final: y, tFinal: t, steps };
}

/**
 * Bitta "tashqi" qadam dt ni ichki maxSubStep dan oshmaydigan bo'laklarga
 * bo'lib integratsiya qiladi. UI 1 s qadam bersa, model barqarorlik uchun
 * 0.1 s ichki qadam ishlatishi mumkin.
 */
export function stepWithSubsteps(
  f: Derivative,
  t: number,
  y: readonly number[],
  dt: number,
  maxSubStep: number,
): number[] {
  const n = Math.max(1, Math.ceil(dt / maxSubStep));
  const h = dt / n;
  let cur = [...y];
  let tc = t;
  for (let i = 0; i < n; i++) {
    cur = rk4Step(f, tc, cur, h);
    tc += h;
  }
  return cur;
}
